import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { ALERTS, EMERGENCY_CALLS } from '@rsos/constants/incidentTypes';
import { ESRI } from '@rsos/constants/mapProviders';
import { MARKER_TYPE_GRAPHIC } from '@rsos/constants/markerTypes';
import useSelectedTheme from '@rsos/hooks/useSelectedTheme';
import { createCircle } from '@rsos/maps/interface/circles';
import { createMarker } from '@rsos/maps/interface/markers';
import { panMapTo } from '@rsos/utils/panMapToCoordinate';
import PopupPortal from '../../PopupPortal';
import '../OverridePOPUP.css';
import AlertPopup from './AlertPopup';
import {
  getAlertMarkerIcon,
  getAlertMarkerSize,
  getAlertCircleColor,
} from './alertMarkerIcons';
import { isRegularAlert } from './utils';

const baseEsriCircle = {
  type: 'simple-fill',
  style: 'solid',
  color: getAlertCircleColor(),
  outline: { width: 2, color: getAlertCircleColor() },
};

/**
 * ESRI MAPS
 * Define Esri maps specific things outside of the React lifecycle
 */
// const portalMount = document.createElement('div');

export const AlertMarkers = ({
  activeQueueTab,
  map,
  alertMarkersLayer,
  alertMarkers,
  setAlertMarkers,
  alertMarkerCirclesLayer,
  alertMarkerCircles,
  setAlertMarkerCircles,
  onMarkerAdd,
  onRemoveID,
  removedID,
  alerts,
  popupData,
  portalMount,
}) => {
  const IDWithNewAlert = useSelector(state => state.incidents.IDWithNewAlert);
  const declinedAlertID = useSelector(state => state.incidents.declinedAlertID);
  const selectedAlertID = useSelector(state => state.incidents.selectedAlertID);
  const selectedCallerID = useSelector(
    state => state.emergencyCalls.selectedCallerID,
  );
  const userProfile = useSelector(state => state.sinatra.user.profile);
  const isAllView = !selectedCallerID && !selectedAlertID;

  const themeName = useSelectedTheme();

  // Handle incoming alerts
  useEffect(() => {
    switch (map.mapProvider) {
      case ESRI:
        if (IDWithNewAlert) {
          const alert = alerts[IDWithNewAlert];
          if (isRegularAlert(alert)) {
            if (isAllView) {
              const latlng = {
                lat: alert?.location?.geodetic?.latitude,
                lng: alert?.location?.geodetic?.longitude,
              };
              const markerIcon = getAlertMarkerIcon(isAllView, false, alert);
              const currentMarker = alertMarkers.find(marker => {
                return marker.custom.alertID === IDWithNewAlert;
              });

              if (currentMarker) {
                const options = {
                  position: latlng,
                };
                currentMarker.updatePosition(options);
                onMarkerAdd();
              } else {
                createMarker({
                  mapObject: map,
                  options: {
                    point: latlng,
                    icon: markerIcon,
                    iconSize: getAlertMarkerSize(isAllView),
                    custom: {
                      alertID: IDWithNewAlert,
                    },
                    layer: alertMarkersLayer,
                  },
                  markerType: MARKER_TYPE_GRAPHIC,
                }).then(resolvedMarker => {
                  alertMarkersLayer.add(resolvedMarker.instance);
                  setAlertMarkers([resolvedMarker, ...alertMarkers]);
                  onMarkerAdd();
                  return resolvedMarker;
                });
              }
            } else if (IDWithNewAlert === selectedAlertID) {
              const { location } = alerts[selectedAlertID];
              const currentMarker = alertMarkers.find(
                marker => marker.custom.alertID === selectedAlertID,
              );

              const latlng = {
                lat: parseFloat(location.geodetic?.latitude),
                lng: parseFloat(location.geodetic?.longitude),
              };

              if (currentMarker) {
                // Create new marker
                const markerIcon = getAlertMarkerIcon(
                  isAllView,
                  true,
                  alerts[currentMarker?.custom?.alertID],
                );
                const markerSize = getAlertMarkerSize(
                  isAllView,
                  true,
                  alerts[currentMarker?.custom?.alertID],
                );
                createMarker({
                  mapObject: map,
                  options: {
                    point: latlng,
                    icon: markerIcon,
                    iconSize: markerSize,
                    custom: {
                      alertID: selectedAlertID,
                    },
                    layer: alertMarkersLayer,
                  },
                  markerType: MARKER_TYPE_GRAPHIC,
                }).then(resolvedMarker => {
                  // Update current marker
                  const newIcon = getAlertMarkerIcon(
                    isAllView,
                    false,
                    alerts[currentMarker?.custom?.alertID],
                  );
                  const markerSize = getAlertMarkerSize(
                    isAllView,
                    false,
                    alerts[currentMarker?.custom?.alertID],
                  );
                  currentMarker.changeMarkerIcon({
                    icon: newIcon,
                    iconSize: markerSize,
                  });
                  panMapTo({
                    mapProvider: ESRI,
                    mapInstance: map.mapInstance,
                    latitude: latlng.lat,
                    longitude: latlng.lng,
                  });

                  onMarkerAdd();
                  alertMarkersLayer.add(resolvedMarker.instance);
                  setAlertMarkers([resolvedMarker, ...alertMarkers]);
                });

                const currentCircle = alertMarkerCircles.find(
                  circle => circle.custom.alertID === selectedAlertID,
                );
                if (currentCircle) {
                  // Old circle - change it to transparent
                  currentCircle.changeCircleColor({
                    symbolOptions: {
                      ...baseEsriCircle,
                      color: getAlertCircleColor(true),
                      outline: { width: 2, color: getAlertCircleColor(true) },
                    },
                  });
                  // New circle
                  const circleOptions = {
                    center: [latlng.lng, latlng.lat],
                    radius:
                      location.geodetic.uncertainty_radius < 0
                        ? 0
                        : parseFloat(location.geodetic.uncertainty_radius),
                  };
                  createCircle({
                    mapObject: map,
                    circleOptions: {
                      ...circleOptions,
                      custom: {
                        alertID: selectedAlertID,
                      },
                      layer: alertMarkerCirclesLayer,
                    },
                    graphicOptions: {
                      symbol: baseEsriCircle,
                      visible: true,
                    },
                  }).then(resolvedMarkerCircle => {
                    alertMarkerCirclesLayer.add(resolvedMarkerCircle.instance);
                    setAlertMarkerCircles([
                      resolvedMarkerCircle,
                      ...alertMarkerCircles,
                    ]);
                  });
                }
              }
            }
          }
        }
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [IDWithNewAlert]);

  useEffect(() => {
    switch (map.mapProvider) {
      case ESRI:
        // Find and remove corresponding marker
        if (alertMarkersLayer && alertMarkers.length && removedID) {
          const foundMarker = alertMarkers.find(marker => {
            return marker.custom.alertID === removedID;
          });
          if (foundMarker) {
            new Promise(resolve => {
              foundMarker.remove();
              resolve();
            }).then(() => {
              setAlertMarkers(
                alertMarkers.filter(
                  marker => marker.custom.alertID !== removedID,
                ),
              );
              onRemoveID();
            });
          }
        }
        break;
      default:
        return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [removedID]);

  useEffect(() => {
    if (declinedAlertID && alertMarkers.length) {
      setAlertMarkers(
        alertMarkers.filter(marker => {
          if (marker.custom.alertID === declinedAlertID) {
            marker.remove();
          }
          return marker.custom.alertID !== declinedAlertID;
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [declinedAlertID]);

  useEffect(() => {
    if (alertMarkersLayer && activeQueueTab === ALERTS) {
      alertMarkersLayer.opacity = 1;
    } else if (alertMarkersLayer && activeQueueTab === EMERGENCY_CALLS) {
      alertMarkersLayer.opacity = 0.5;
    }
  }, [activeQueueTab, alertMarkersLayer]);

  // Conditional rendering based on map provider
  switch (map.mapProvider) {
    case ESRI:
      if (popupData && isRegularAlert(popupData.alert)) {
        const createdAt =
          popupData.alert.location_history[popupData.index].created_at;
        return (
          <PopupPortal mount={portalMount}>
            <AlertPopup
              userProfile={userProfile}
              isAllView={isAllView}
              data={popupData.alert}
              createdAt={createdAt}
              themeName={themeName}
            />
          </PopupPortal>
        );
      }
      return null;
    default:
      return null;
  }
};
